import React, { useState } from 'react';
import { Users, UtensilsCrossed, Sparkles, CalendarCheck, Phone, Mail } from 'lucide-react';
import { eventFeatures } from '../mock';

const Events = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    eventType: '',
    guestCount: '',
    message: ''
  });

  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    console.log('Event booking form submitted:', formData);
    setFormSubmitted(true);
    setTimeout(() => {
      setFormSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        eventDate: '',
        eventType: '',
        guestCount: '',
        message: ''
      });
    }, 3000);
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=1600')" }}>
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Celebrate Life's Special Moments with Us
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            Our private party hall is perfect for birthdays, anniversaries, and all your special celebrations
          </p>
        </div>
      </section>

      {/* Event Space Overview */}
      <section className="section-padding">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=800"
                alt="Party Hall"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: "'Playfair Display', serif", color: '#2C3E50' }}>
                Your Perfect Event Venue
              </h2>
              <div className="space-y-4 text-gray-700 text-lg">
                <p>
                  Our elegant party hall provides the perfect setting for your special occasions. With comfortable seating for 40-50 guests, we offer an intimate yet spacious venue for your celebrations.
                </p>
                <p>
                  Whether it's a birthday party, anniversary celebration, corporate gathering, or family reunion, our experienced team will help you create memories that last a lifetime.
                </p>
                <div className="flex items-center space-x-4 text-[#E84118] font-semibold mt-6">
                  <Users className="w-6 h-6" />
                  <span>Capacity: 40-50 Guests</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Features */}
      <section className="section-padding bg-[#FFF8F0]">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif", color: '#2C3E50' }}>
              Event Features & Amenities
            </h2>
            <p className="text-gray-600 text-lg">
              Everything you need for a successful celebration
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {eventFeatures.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md text-center hover-lift">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#E84118] flex items-center justify-center text-white">
                  {feature.icon === 'Users' && <Users className="w-8 h-8" />}
                  {feature.icon === 'UtensilsCrossed' && <UtensilsCrossed className="w-8 h-8" />}
                  {feature.icon === 'Sparkles' && <Sparkles className="w-8 h-8" />}
                  {feature.icon === 'CalendarCheck' && <CalendarCheck className="w-8 h-8" />}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-[#2C3E50]">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-white p-8 rounded-lg shadow-md">
            <h3 className="text-2xl font-bold mb-4 text-center" style={{ fontFamily: "'Playfair Display', serif", color: '#2C3E50' }}>
              Additional Amenities
            </h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
              <li className="flex items-center space-x-2">
                <span className="text-[#E84118]">✓</span>
                <span>Easy accessibility and ample parking</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-[#E84118]">✓</span>
                <span>Professional event planning support</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-[#E84118]">✓</span>
                <span>Customizable menu options</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-[#E84118]">✓</span>
                <span>Dedicated service staff</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-[#E84118]">✓</span>
                <span>Flexible timing arrangements</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="text-[#E84118]">✓</span>
                <span>Reasonable rental rates</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Booking Process */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif", color: '#2C3E50' }}>
              Simple 3-Step Booking Process
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#E84118] flex items-center justify-center text-white text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-semibold mb-2 text-[#2C3E50]">Contact Us</h3>
              <p className="text-gray-600">Fill out the inquiry form or call us directly</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#E84118] flex items-center justify-center text-white text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-semibold mb-2 text-[#2C3E50]">Plan Together</h3>
              <p className="text-gray-600">Discuss your requirements and customize the menu</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#E84118] flex items-center justify-center text-white text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-semibold mb-2 text-[#2C3E50]">Celebrate!</h3>
              <p className="text-gray-600">Relax and enjoy your special day with us</p>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section-padding bg-[#FFF8F0]">
        <div className="container">
          <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-lg">
            <h2 className="text-3xl font-bold mb-6 text-center" style={{ fontFamily: "'Playfair Display', serif", color: '#2C3E50' }}>
              Book Your Event
            </h2>

            {formSubmitted ? (
              <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg text-center">
                <p className="font-semibold mb-2">Thank you for your inquiry!</p>
                <p>Our team will contact you shortly to discuss your event details.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E84118] focus:border-transparent"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E84118] focus:border-transparent"
                      placeholder="+91 98765 43210"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E84118] focus:border-transparent"
                    placeholder="john@example.com"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Event Date *</label>
                    <input
                      type="date"
                      name="eventDate"
                      value={formData.eventDate}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E84118] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Event Type *</label>
                    <select
                      name="eventType"
                      value={formData.eventType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E84118] focus:border-transparent"
                    >
                      <option value="">Select type</option>
                      <option value="birthday">Birthday Party</option>
                      <option value="anniversary">Anniversary</option>
                      <option value="corporate">Corporate Event</option>
                      <option value="family">Family Gathering</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Expected Guest Count *</label>
                  <input
                    type="number"
                    name="guestCount"
                    value={formData.guestCount}
                    onChange={handleChange}
                    required
                    min="1"
                    max="50"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E84118] focus:border-transparent"
                    placeholder="e.g., 40"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Special Requirements</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E84118] focus:border-transparent"
                    placeholder="Tell us about any special requirements, dietary restrictions, or preferences..."
                  ></textarea>
                </div>

                <button type="submit" className="btn-primary w-full">
                  Submit Inquiry
                </button>
              </form>
            )}

            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-gray-700 mb-4">Prefer to talk directly? We're here to help!</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:+919876543210" className="btn-gold inline-flex items-center justify-center space-x-2">
                  <Phone className="w-5 h-5" />
                  <span>Call Us</span>
                </a>
                <a href="mailto:info@thegrill.com" className="btn-secondary inline-flex items-center justify-center space-x-2">
                  <Mail className="w-5 h-5" />
                  <span>Email Us</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Events;
